import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import useQuery from 'hooks/useQuery'
import { filterEmptyValues } from 'utils/helpers'
import {
  AggregatorDropdown,
  Button,
  Dropdown,
  TextInput,
} from 'components/common'
import { ORDER_STATUS, ORDER_TABLE_FIELDS } from 'utils/constants'
import { CustomObject } from 'types/common'
import { FILTER_FIELD_ORDERS } from './utils'
import StoreDropdown from 'components/userManagement/storeDropdown'
import { DateTime } from 'components/common/input/dateTime'

const {
  ORDER_NO,
  ORDER_SHORTCODE,
  AGGREGATOR_CODE,
  INVOICE_NO,
  STORE_CODE,
  ERP_SYNC,
  LAST_MILE_SYNC,
  AREA,
  PREPARED_BY,
  STATUS,
} = ORDER_TABLE_FIELDS

type Props = {
  toggleFilterForm: () => void
}

const OrderFilterForm = ({ toggleFilterForm }: Props) => {
  const { getQuery, removeQuery, setMultipleQueries } = useQuery()
  const filters = getQuery(FILTER_FIELD_ORDERS)

  const [resetFilter, setResetFilter] = useState(false)

  const { register, handleSubmit, setValue, reset } = useForm({
    defaultValues: filters,
  })

  const orderStatusOptions = useMemo(() => {
    const statuses = Object.values(ORDER_STATUS)

    const options = statuses.map((status) => ({
      label: status,
      value: status,
      disabled: false,
    }))

    options.unshift({ label: '--- NONE ---', value: 'NONE', disabled: false })

    return options
  }, [])

  const onFilterApply = (data: CustomObject) => {
    if (data?.[AGGREGATOR_CODE.value] === 'NONE') {
      delete data[AGGREGATOR_CODE.value]
    }

    if (data?.[STORE_CODE.value] === 'NONE') {
      delete data[STORE_CODE.value]
    }

    if (data?.[ERP_SYNC.value] === 'NONE') {
      delete data[ERP_SYNC.value]
    }

    if (data?.[LAST_MILE_SYNC.value] === 'NONE') {
      delete data[LAST_MILE_SYNC.value]
    }

    if (data?.[STATUS.value] === 'NONE') {
      delete data[STATUS.value]
    }

    const filteredData = filterEmptyValues(data)

    if (Object.values(filteredData).length > 0) {
      setMultipleQueries({ [FILTER_FIELD_ORDERS]: filteredData, page: 1 })
    } else {
      removeQuery(FILTER_FIELD_ORDERS)
    }

    toggleFilterForm()
  }

  const cancelFilter = () => {
    removeQuery(FILTER_FIELD_ORDERS)
    reset()
    setResetFilter((pre) => !pre)
    toggleFilterForm()
  }

  return (
    <div className="mt-2 rounded-lg border bg-white px-5 py-3 ">
      <div className="mx-auto max-w-5xl">
        <form onSubmit={handleSubmit(onFilterApply)}>
          <div className="grid grid-cols-2 gap-x-14 gap-y-3 md:grid-cols-3">
            {/* ORDER_NO */}
            <TextInput
              register={register}
              name={ORDER_NO.value}
              label={ORDER_NO.label}
            />

            {/* Invoice  */}
            <TextInput
              register={register}
              name={INVOICE_NO.value}
              label={INVOICE_NO.label}
            />

            {/* Area */}
            <TextInput
              register={register}
              name={AREA.value}
              label={AREA.label}
            />

            {/* Channel */}
            <AggregatorDropdown
              withNoneOption
              setValue={setValue}
              defaultValue={filters?.[AGGREGATOR_CODE.value] || resetFilter}
              name={AGGREGATOR_CODE.value}
              label={AGGREGATOR_CODE.label}
            />

            {/* Store */}
            <StoreDropdown
              withNoneOption
              setValue={setValue}
              defaultValue={filters?.[STORE_CODE.value] || resetFilter}
              name={STORE_CODE.value}
              label={STORE_CODE.label}
            />

            {/* Order Status */}
            <Dropdown
              name={STATUS.value}
              label={STATUS.label}
              setValue={setValue}
              defaultValue={filters?.[STATUS.value] || resetFilter}
              options={orderStatusOptions}
            />

            {/* ERP Sync */}
            <Dropdown
              name={ERP_SYNC.value}
              label={ERP_SYNC.label}
              setValue={setValue}
              defaultValue={filters?.[ERP_SYNC.value] || resetFilter}
              options={[
                { label: '--- NONE ---', value: 'NONE', disabled: false },
                { label: 'Yes', value: 'true', disabled: false },
                {
                  label: 'No',
                  value: 'false',
                  disabled: false,
                },
              ]}
            />

            {/* LAST Mile Sync */}
            <Dropdown
              name={LAST_MILE_SYNC.value}
              label={LAST_MILE_SYNC.label}
              setValue={setValue}
              defaultValue={filters?.[LAST_MILE_SYNC.value] || resetFilter}
              options={[
                { label: '--- NONE ---', value: 'NONE', disabled: false },
                { label: 'Yes', value: 'true', disabled: false },
                {
                  label: 'No',
                  value: 'false',
                  disabled: false,
                },
              ]}
            />

            <TextInput
              register={register}
              name={PREPARED_BY.value}
              label={PREPARED_BY.label}
            />

            <DateTime label={'Orders From'} name={'from'} register={register} />

            <DateTime label={'Orders To'} name={'to'} register={register} />

            <TextInput
              register={register}
              name={ORDER_SHORTCODE.value}
              label={ORDER_SHORTCODE.label}
            />
          </div>
          <div className="my-4 flex gap-5">
            <Button buttonText="Apply Filters" className="w-32" />

            <Button
              buttonText="cancel"
              variant="outline"
              type="button"
              onClick={cancelFilter}
              className="w-32"
            />
          </div>
        </form>
      </div>
    </div>
  )
}

export default OrderFilterForm
